import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Page, StatTiles } from '../../components/shell/Page';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SplitLayout, { paneForm, PaneFooter } from '../../components/shell/SidePane';
import PasswordField, { RequireChangeCheckbox, CopyField } from '../../components/shell/PasswordField';
import { Chalkboard, Plus, Copy, Check, Envelope, Trash, Key, ToggleRight, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function InstructorManagement() {
  const { api } = useAuth();
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  
  const [createForm, setCreateForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    require_change: true
  });
  
  const [inviteForm, setInviteForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    send_email: false,
    require_change: true
  });

  useEffect(() => {
    fetchInstructors();
  }, []);

  const fetchInstructors = async () => {
    try {
      const response = await api.get('/instructors');
      setInstructors(response.data);
    } catch (error) {
      toast.error('Failed to load instructors');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (instructorId) => {
    if (!window.confirm('Are you sure you want to delete this instructor? This action cannot be undone.')) return;
    try {
      await api.delete(`/users/${instructorId}`);
      toast.success('Instructor deleted');
      fetchInstructors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete instructor');
    }
  };

  const handleToggleStatus = async (instructorId) => {
    try {
      const res = await api.put(`/users/${instructorId}/status`);
      toast.success(res.data?.message || 'Status updated');
      fetchInstructors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateLoading(true);

    try {
      const response = await api.post('/instructors/create', createForm.password ? createForm : { ...createForm, password: undefined });
      setCreatedCredentials(response.data);
      fetchInstructors();
      toast.success('Instructor created successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create instructor';
      toast.error(message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);

    try {
      const response = await api.post('/instructors/invite', inviteForm);
      setCreatedCredentials(response.data);
      fetchInstructors();
      toast.success('Instructor invited successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to invite instructor';
      toast.error(message);
    } finally {
      setInviteLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const resetCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateForm({ first_name: '', last_name: '', email: '', password: '', require_change: true });
    setCreatedCredentials(null);
  };

  const resetInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteForm({ first_name: '', last_name: '', email: '', send_email: false, require_change: true });
    setCreatedCredentials(null);
  };

  const CopyButton = ({ text, field }) => (
    <button
      type="button"
      onClick={() => copyToClipboard(text, field)}
      className="p-1.5 rounded hover:bg-muted transition-colors"
      title="Copy to clipboard"
    >
      {copiedField === field ? (
        <Check className="w-4 h-4 text-green-600" />
      ) : (
        <Copy className="w-4 h-4 text-muted-foreground" />
      )}
    </button>
  );

  // ─── Side pane (create / invite open here instead of popups) ───
  const closePane = () => { resetCreateDialog(); resetInviteDialog(); };
  const openPane = (which) => { closePane(); which(true); };
  let side = null;
  if (inviteDialogOpen) {
    if (createdCredentials) {
      // after a successful invite the pane shows the credentials + a Done footer
      side = {
        open: true, onOpenChange: closePane, title: 'Invite instructor', subtitle: 'Send an invitation to a new instructor', icon: Envelope, testid: 'invite-instructor-pane',
        children: (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3" data-testid="invite-result">
            <p className="text-sm font-medium text-green-800">Instructor invited successfully!</p>

            <div className="space-y-2">
              <div className="flex items-center justify-between bg-white rounded p-2">
                <div className="min-w-0">
                  <p className="field-hint">Email</p>
                  <p className="text-sm font-mono">{createdCredentials.email}</p>
                </div>
                <CopyButton text={createdCredentials.email} field="invite-email" />
              </div>

              {createdCredentials.temp_password && (
                <div className="flex items-center justify-between bg-white rounded p-2">
                  <div className="min-w-0">
                    <p className="field-hint">Temporary Password</p>
                    <p className="text-sm font-mono">{createdCredentials.temp_password}</p>
                  </div>
                  <CopyButton text={createdCredentials.temp_password} field="invite-password" />
                </div>
              )}

              {createdCredentials.invitation_link && (
                <div className="flex items-center justify-between bg-white rounded p-2">
                  <div className="min-w-0">
                    <p className="field-hint">Invitation Link</p>
                    <p className="text-sm font-mono truncate max-w-[260px]">{createdCredentials.invitation_link}</p>
                  </div>
                  <CopyButton text={createdCredentials.invitation_link} field="invite-link" />
                </div>
              )}
            </div>

            <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
              Share these credentials securely with the instructor.
            </p>
          </div>
        ),
        footer: <PaneFooter onSubmit={resetInviteDialog} submitLabel="Done" submitIcon={Check} />,
      };
    } else {
      const f = paneForm({ id: 'invite-instructor-form', onSubmit: handleInvite, onCancel: resetInviteDialog, submitLabel: 'Send invitation', submitting: inviteLoading, submitIcon: PaperPlaneTilt, children: (
        <>
        <div className="pane-grid">
          <div className="field">
            <Label>First Name</Label>
            <Input
              value={inviteForm.first_name}
              onChange={(e) => setInviteForm(prev => ({ ...prev, first_name: e.target.value }))}
              required
              data-testid="invite-first-name-input"
            />
          </div>
          <div className="field">
            <Label>Last Name</Label>
            <Input
              value={inviteForm.last_name}
              onChange={(e) => setInviteForm(prev => ({ ...prev, last_name: e.target.value }))}
              required
              data-testid="invite-last-name-input"
            />
          </div>
        </div>

        <div className="field">
          <Label>Email</Label>
          <Input
            type="email"
            value={inviteForm.email}
            onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
            required
            data-testid="invite-email-input"
          />
        </div>
        <RequireChangeCheckbox
          checked={inviteForm.require_change}
          onCheckedChange={(v) => setInviteForm(prev => ({ ...prev, require_change: v }))}
          testid="invite-require-change"
        />
        </>
      ) });
      side = { open: true, onOpenChange: closePane, title: 'Invite instructor', subtitle: 'Send an invitation to a new instructor', icon: Envelope, children: f.body, footer: f.footer, testid: 'invite-instructor-pane' };
    }
  } else if (createDialogOpen) {
    if (createdCredentials) {
      // after a successful create the pane shows the credentials + a Done footer
      side = {
        open: true, onOpenChange: closePane, title: 'New instructor', subtitle: 'Create a new instructor account with default credentials', icon: Chalkboard, testid: 'create-instructor-pane',
        children: (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3" data-testid="create-result">
            <p className="text-sm font-medium text-green-800">Instructor created successfully!</p>

            <div className="space-y-2">
              <div className="flex items-center justify-between bg-white rounded p-2">
                <div className="min-w-0">
                  <p className="field-hint">Email</p>
                  <p className="text-sm font-mono">{createdCredentials.email}</p>
                </div>
                <CopyButton text={createdCredentials.email} field="create-email" />
              </div>

              <div className="bg-white rounded p-2">
                <CopyField label="Password" value={createdCredentials.default_password} secret revealed testid="create-result-password" />
              </div>

              <div className="flex items-center justify-between bg-white rounded p-2">
                <div className="min-w-0">
                  <p className="field-hint">Login URL</p>
                  <p className="text-sm font-mono truncate max-w-[260px]">{createdCredentials.login_url}</p>
                </div>
                <CopyButton text={createdCredentials.login_url} field="create-url" />
              </div>
            </div>

            <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
              {createdCredentials.require_change
                ? 'Share these credentials securely. The instructor must set a new password at first login.'
                : 'Share these credentials securely. The instructor can change the password under My profile.'}
            </p>
          </div>
        ),
        footer: <PaneFooter onSubmit={resetCreateDialog} submitLabel="Done" submitIcon={Check} />,
      };
    } else {
      const f = paneForm({ id: 'create-instructor-form', onSubmit: handleCreate, onCancel: resetCreateDialog, submitLabel: 'Create instructor', submitting: createLoading, submitIcon: Plus, children: (
        <>
        <div className="pane-grid">
          <div className="field">
            <Label>First Name</Label>
            <Input
              value={createForm.first_name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, first_name: e.target.value }))}
              required
              data-testid="create-first-name-input"
            />
          </div>
          <div className="field">
            <Label>Last Name</Label>
            <Input
              value={createForm.last_name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, last_name: e.target.value }))}
              required
              data-testid="create-last-name-input"
            />
          </div>
        </div>

        <div className="field">
          <Label>Email</Label>
          <Input
            type="email"
            value={createForm.email}
            onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
            required
            data-testid="create-email-input"
          />
        </div>

        <PasswordField
          id="create-password"
          label={<>Password <span className="text-muted-foreground font-normal">(optional)</span></>}
          generate
          value={createForm.password}
          onChange={(v) => setCreateForm(prev => ({ ...prev, password: v }))}
          placeholder="Leave blank to generate one"
          hint="Shown once after creation — copy it and share it securely with the instructor."
          testid="create-password-input"
        />
        <RequireChangeCheckbox
          checked={createForm.require_change}
          onCheckedChange={(v) => setCreateForm(prev => ({ ...prev, require_change: v }))}
          testid="create-require-change"
        />
        </>
      ) });
      side = { open: true, onOpenChange: closePane, title: 'New instructor', subtitle: 'Create a new instructor account with default credentials', icon: Chalkboard, children: f.body, footer: f.footer, testid: 'create-instructor-pane' };
    }
  }

  return (
    <DashboardLayout>
      <SplitLayout side={side}>
      <Page data-testid="instructor-management-page">
        <StatTiles items={[
          { label: 'Total instructors', value: instructors.length, icon: Chalkboard },
          { label: 'Active', value: instructors.filter(i => i.is_active).length, icon: Check, iconClass: 'bg-emerald-100 text-emerald-700' },
          { label: 'Pending setup', value: instructors.filter(i => i.must_change_password).length, icon: Key, iconClass: 'bg-amber-100 text-amber-700' },
        ]} />

        <DataGrid
          title="Instructors"
          rows={instructors}
          loading={loading}
          onRefresh={fetchInstructors}
          searchKeys={['email', 'first_name', 'last_name']}
          columns={[
            {
              key: 'name', header: 'Instructor', accessor: (i) => `${i.first_name || ''} ${i.last_name || ''}`.trim(),
              render: (i) => (
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-shell-primary/10 text-shell-primary flex items-center justify-center text-[11px] font-semibold">
                    {`${i.first_name?.[0] || ''}${i.last_name?.[0] || ''}`.toUpperCase()}
                  </div>
                  <span className="font-medium">{i.first_name} {i.last_name}</span>
                  {i.must_change_password && <StatusBadge value="Needs setup" tone="warning" />}
                </div>
              ),
            },
            { key: 'email', header: 'Email', accessor: (i) => i.email || '', className: 'text-muted-foreground' },
            { key: 'is_active', header: 'Status', width: 110, accessor: (i) => i.is_active !== false, render: (i) => <StatusBadge value={i.is_active !== false} /> },
            { key: 'created_at', header: 'Joined', width: 120, render: (i) => (i.created_at ? new Date(i.created_at).toLocaleDateString() : '—') },
          ]}
          rowTone={(i) => (i.is_active === false ? 'muted' : null)}
          primaryActions={(
            <>
              <Button size="sm" className="h-9" onClick={() => openPane(setCreateDialogOpen)} data-testid="create-instructor-btn">
                <Plus className="w-4 h-4" /> New instructor
              </Button>
              <Button size="sm" variant="outline" className="h-9" onClick={() => openPane(setInviteDialogOpen)} data-testid="invite-instructor-btn">
                <Envelope className="w-4 h-4" /> Invite
              </Button>
            </>
          )}
          selectionActions={[
            { key: 'toggle', label: 'Activate / Deactivate', icon: ToggleRight, onClick: ([i]) => handleToggleStatus(i.id) },
            { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: ([i]) => handleDelete(i.id) },
          ]}
          emptyMessage="No instructors yet. Create or invite instructors to get started."
        />
      </Page>
      </SplitLayout>
    </DashboardLayout>
  );
}
